// Scrub bearer tokens out of everything odv-truest sends to Sentry (errors, transactions,
// breadcrumbs, replay). Public link routes carry the secret in the path or query string.
// Librarian #3030 review 2026-10-21: /certification/hook/<token> and ?token= reached Sentry.

// Path segments that follow these prefixes are bearer tokens (or guest-facing ids) → ':token'.
export const TOKEN_PATH_PREFIXES = [
  'certification/hook', 'join/schedule', 'sign', 'event', 'quick-accept', 'sub',
  'ticket', 'tip', 'invoice', 'sign-document', 'accept-event-quote', 'event-contract',
  'claim', 'claims', 'portal/claim', 'trainee-clockin', 'trainer-checklist', 'd2-scheduling',
];
// Query/hash params that carry secrets (magic link, PKCE, recovery, link tokens).
export const SECRET_PARAMS = [
  'token', 'token_hash', 'code', 'access_token', 'refresh_token', 'provider_token',
  'provider_refresh_token', 'hook_token', 'otp', 'key', 't', 'm', 'signature', 'sig',
];

const esc = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const PATH_RE = new RegExp(
  `(^|/)(${[...TOKEN_PATH_PREFIXES].sort((a, b) => b.length - a.length).map(esc).join('|')})/([^/?#]+)`, 'g');
const SECRET_SET = new Set(SECRET_PARAMS);

function scrubParams(str) {
  return str.replace(/([?&#])([^=&#]+)=([^&#]*)/g, (m, sep, k, v) =>
    (SECRET_SET.has(decodeURIComponentSafe(k).toLowerCase()) && v ? `${sep}${k}=[Filtered]` : m));
}
function decodeURIComponentSafe(s) { try { return decodeURIComponent(s); } catch { return s; } }

export function scrubUrl(value) {
  if (typeof value !== 'string' || !value) return value;
  return scrubParams(value.replace(PATH_RE, (m, lead, prefix) => `${lead}${prefix}/:token`));
}

export function isTokenRoute(pathname) {
  if (typeof pathname !== 'string') return false;
  PATH_RE.lastIndex = 0;
  return new RegExp(PATH_RE.source).test(pathname);
}

function scrubDeep(obj, depth = 0) {
  if (obj == null || depth > 6) return obj;
  if (typeof obj === 'string') return scrubUrl(obj);
  if (Array.isArray(obj)) return obj.map((v) => scrubDeep(v, depth + 1));
  if (typeof obj === 'object') {
    for (const k of Object.keys(obj)) obj[k] = scrubDeep(obj[k], depth + 1);
  }
  return obj;
}

// Header names whose value is always a credential (matched case-insensitively) → '[Filtered]'.
export const SECRET_HEADERS = [
  'authorization', 'proxy-authorization', 'cookie', 'set-cookie', 'apikey', 'x-api-key',
  'x-recruit-access-token', 'x-recruit-access-scope', 'x-recruit-access-context',
  'x-supabase-auth', 'x-client-info', 'x-csrf-token', 'x-xsrf-token',
];
const SECRET_HEADER_SET = new Set(SECRET_HEADERS);

// Every header value is URL-scrubbed (Referer, Origin, custom); credential headers are dropped to [Filtered].
export function scrubHeaders(headers) {
  if (!headers || typeof headers !== 'object') return headers;
  const out = {};
  for (const [k, v] of Object.entries(headers)) {
    const name = String(k).toLowerCase();
    out[k] = SECRET_HEADER_SET.has(name) || /token|secret|session|auth/.test(name)
      ? '[Filtered]'
      : (typeof v === 'string' ? scrubUrl(v) : scrubDeep(v));
  }
  return out;
}

export function scrubEvent(event) {
  if (!event) return event;
  if (event.request) {
    if (event.request.url) event.request.url = scrubUrl(event.request.url);
    if (event.request.query_string) event.request.query_string = typeof event.request.query_string === 'string'
      ? scrubParams(`?${event.request.query_string}`).slice(1) : '[Filtered]';
    if (event.request.headers) event.request.headers = scrubHeaders(event.request.headers);
    if ('cookies' in event.request) delete event.request.cookies;
  }
  if (event.transaction) event.transaction = scrubUrl(event.transaction);
  if (Array.isArray(event.urls)) event.urls = event.urls.map(scrubUrl); // replay_event
  if (Array.isArray(event.breadcrumbs)) event.breadcrumbs = event.breadcrumbs.map(scrubBreadcrumb);
  if (Array.isArray(event.spans)) for (const s of event.spans) { if (s.description) s.description = scrubUrl(s.description); if (s.data) scrubDeep(s.data); }
  if (event.contexts?.trace?.data) scrubDeep(event.contexts.trace.data);
  if (event.tags) scrubDeep(event.tags);
  if (event.extra) scrubDeep(event.extra);
  if (event.exception?.values) for (const v of event.exception.values) if (v.value) v.value = scrubUrl(v.value);
  if (event.message) event.message = scrubUrl(event.message);
  return event;
}

export function scrubBreadcrumb(crumb) {
  if (!crumb) return crumb;
  if (crumb.message) crumb.message = scrubUrl(crumb.message);
  if (crumb.data) scrubDeep(crumb.data); // navigation from/to, fetch/xhr url
  return crumb;
}

// Replay: never capture a replay on a token route; drop any recording event that names one.
export function shouldReplay(pathname) { return !isTokenRoute(pathname); }

export function scrubRecordingEvent(ev) {
  const href = typeof window !== 'undefined' ? window.location?.pathname : '';
  if (isTokenRoute(href)) return null;
  return scrubDeep(ev);
}
